import type { Grade } from '../types/domain';
import type { MemberNoteRecord, MemberNoteType } from '../types/pointItemNote';
import { ANNUAL_WEEKDAY_KEYS, buildAnnualPeriodLabels, type AnnualClassEntry } from './annualTimetable';
import { mondayIsoForDateInLocal, dateToWeekdayKey } from './dashboardDateContext';
import { resolveAnnualProgressKeysForDate } from './dashboardTermResolve';
import { parseISODate } from './date';
import {
  formatScheduleTimeTagLabel,
  getScheduleTimeTagsForGrade,
  resolveDisplayTimeTagLabel,
  splitScheduleTimeTagLabel,
} from './pointItemScheduleTimeTags';
import type { ProgramProgressRow } from './programProgressRows';

export type PointNoteMemoRecordKind = 'point_general' | 'point_learning' | 'point_behavior';

export type PointNoteMemoRowInput = {
  key: string;
  termNumber: 1 | 2;
  sortKey: string;
  hasProgramSchedule: boolean;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  programLabel: string;
  programDotKind: 'fixed' | 'project' | 'track' | 'none';
  timetableText: string;
  unitLabel: string;
  learningLabel: string;
  recordKind: PointNoteMemoRecordKind;
  kindLabel: MemberNoteType;
  note: string;
  recordMemberGroupId: string | null;
  /** 포인트 코인 아이템 멤버 기록 — 멤버 필터용 */
  sourceMemberId: string;
};

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

export function pointNoteTypeToRecordKind(noteType: MemberNoteType): PointNoteMemoRecordKind {
  if (noteType === '학습') return 'point_learning';
  if (noteType === '행동') return 'point_behavior';
  return 'point_general';
}

export function pointNoteRecordKindLabel(kind: string): string {
  if (kind === 'point_general') return '일반';
  if (kind === 'point_learning') return '학습';
  if (kind === 'point_behavior') return '행동';
  return '';
}

function shortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

function stripDayBracketPrefix(label: string): string {
  return label.replace(/^\[[일월화수목금토]\]\s*/, '').trim();
}

function normalizeTagBody(label: string): string {
  const display = (label || '').trim();
  if (!display) return '';
  const { name } = splitScheduleTimeTagLabel(stripDayBracketPrefix(display));
  return stripDayBracketPrefix(name || display);
}

/** 타임태그 라벨 → 교시 슬롯(0-based). 매칭 실패 시 periodLabel만 채움 */
function resolvePeriodFromTimeTag(params: {
  timeTagLabel: string;
  recordedDate: string;
  createdAt?: string;
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  periodLabels: string[];
}): { slotIndex: number; periodLabel: string } {
  const display =
    resolveDisplayTimeTagLabel({
      timeTagLabel: params.timeTagLabel,
      recordedDate: params.recordedDate,
      createdAt: params.createdAt,
    }) ?? '';
  const body = normalizeTagBody(display);
  const date = parseISODate(params.recordedDate);
  const tags =
    !Number.isNaN(date.getTime())
      ? getScheduleTimeTagsForGrade({
          workspaceId: params.workspaceId,
          gradeId: params.gradeId,
          grades: params.grades,
          date,
        })
      : [];

  for (let i = 0; i < tags.length; i += 1) {
    const tagName = (tags[i]?.name ?? '').trim();
    if (!tagName) continue;
    if (body === tagName || body.startsWith(tagName) || tagName.startsWith(body)) {
      return {
        slotIndex: i,
        periodLabel: params.periodLabels[i] || tagName || `${i + 1}타임`,
      };
    }
    const formatted = formatScheduleTimeTagLabel(tags[i]);
    if (display && (display === formatted || body === normalizeTagBody(formatted))) {
      return {
        slotIndex: i,
        periodLabel: params.periodLabels[i] || tagName || `${i + 1}타임`,
      };
    }
  }

  const periodMatch = /^(\d+)\s*교시/.exec(body);
  if (periodMatch) {
    const idx = Number(periodMatch[1]) - 1;
    if (Number.isFinite(idx) && idx >= 0) {
      return {
        slotIndex: idx,
        periodLabel: params.periodLabels[idx] || body || `${idx + 1}타임`,
      };
    }
  }

  const timeMatch = /^(\d{1,2})시(?:\s*(\d{1,2})분)?/.exec(body);
  if (timeMatch && tags.length) {
    const h = Number(timeMatch[1]);
    const m = timeMatch[2] != null ? Number(timeMatch[2]) : 0;
    const target = h * 60 + (Number.isFinite(m) ? m : 0);
    let bestIdx = -1;
    let bestDelta = Infinity;
    for (let i = 0; i < tags.length; i += 1) {
      const startM = /^(\d{1,2}):(\d{2})$/.exec((tags[i]?.startTime || '').trim());
      if (!startM) continue;
      const start = Number(startM[1]) * 60 + Number(startM[2]);
      const d = Math.abs(start - target);
      if (d < bestDelta) {
        bestDelta = d;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0 && bestDelta <= 30) {
      return {
        slotIndex: bestIdx,
        periodLabel:
          params.periodLabels[bestIdx] || (tags[bestIdx]?.name ?? '').trim() || body || `${bestIdx + 1}타임`,
      };
    }
  }

  return {
    slotIndex: 90,
    periodLabel: body || (display ? stripDayBracketPrefix(display) : '—'),
  };
}

export function buildPointNoteMemoRows(params: {
  notes: Array<MemberNoteRecord & { groupId: string }>;
  membersById: Map<string, { id: string; name: string }>;
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  academicYear: number;
  selectedEntry: AnnualClassEntry | null;
  rowsTerm1: ProgramProgressRow[];
  rowsTerm2: ProgramProgressRow[];
  /** 기록 조회 그룹 필터 — 호출측에서 걸러도 되고 여기서도 가능 */
  selectedRecordGroupFilterId?: string;
  allRecordGroupsFilterId?: string;
  generalRecordGroupsFilterId?: string;
}): PointNoteMemoRowInput[] {
  const {
    notes,
    membersById,
    workspaceId,
    gradeId,
    grades,
    academicYear,
    selectedEntry,
    rowsTerm1,
    rowsTerm2,
  } = params;
  const allGroupsId = params.allRecordGroupsFilterId ?? '__all_record_groups__';
  const generalGroupsId = params.generalRecordGroupsFilterId ?? '__general_record_groups__';
  const groupFilter = params.selectedRecordGroupFilterId ?? allGroupsId;

  const periodLabels = selectedEntry ? buildAnnualPeriodLabels(selectedEntry.sectionRows) : [];
  const scheduleByTerm = {
    1: new Map(rowsTerm1.map((r) => [`${r.mondayIso}|${r.weekdayKey}|${r.slotIndex}`, r] as const)),
    2: new Map(rowsTerm2.map((r) => [`${r.mondayIso}|${r.weekdayKey}|${r.slotIndex}`, r] as const)),
  } as const;

  const out: PointNoteMemoRowInput[] = [];

  for (const note of notes) {
    const text = (note.text ?? '').trim();
    if (!text) continue;

    const groupId = (note.groupId ?? '').trim();
    if (groupFilter !== allGroupsId) {
      if (groupFilter === generalGroupsId) continue;
      if (groupId !== groupFilter) continue;
    }

    const dateIso = (note.recordedDate ?? '').trim();
    if (!dateIso) continue;
    const date = parseISODate(dateIso);
    if (Number.isNaN(date.getTime())) continue;

    const termResolved = resolveAnnualProgressKeysForDate(workspaceId, dateIso, academicYear);
    if (termResolved.academicYear !== academicYear) continue;
    const termNumber = termResolved.termNumber;

    const mondayIso = mondayIsoForDateInLocal(date);
    const weekdayKey = dateToWeekdayKey(date);
    const weekdayLabel =
      weekdayKey != null
        ? (ANNUAL_WEEKDAY_KEYS.find((w) => w.key === weekdayKey)?.label ?? weekdayKey)
        : WEEKDAY_KO[date.getDay()] ?? '—';
    const dateLabel = shortDateLabel(dateIso);

    const period = resolvePeriodFromTimeTag({
      timeTagLabel: note.timeTagLabel ?? '',
      recordedDate: dateIso,
      createdAt: note.createdAt,
      workspaceId,
      gradeId,
      grades,
      periodLabels,
    });

    let scheduleRow: ProgramProgressRow | undefined;
    if (weekdayKey != null) {
      scheduleRow = scheduleByTerm[termNumber].get(`${mondayIso}|${weekdayKey}|${period.slotIndex}`);
    }

    const member = membersById.get(note.memberId);
    const memberName = (member?.name ?? '').trim() || '이름 없음';
    const noteBody = `#${memberName} ${text}`;
    const noteType: MemberNoteType =
      note.noteType === '학습' || note.noteType === '행동' ? note.noteType : '일반';
    const recordKind = pointNoteTypeToRecordKind(noteType);
    const slotPad = String(Math.max(0, period.slotIndex)).padStart(3, '0');
    const createdPad = (note.createdAt || '').trim() || note.id;

    out.push({
      key: `point-note:${note.id}`,
      termNumber,
      sortKey: `${termNumber}|${dateIso}|${slotPad}|${createdPad}`,
      hasProgramSchedule: true,
      dateLabel,
      weekdayLabel,
      periodLabel: scheduleRow?.periodLabel || period.periodLabel,
      programLabel: scheduleRow
        ? scheduleRow.programKindLabel === '고정'
          ? '행사'
          : scheduleRow.programKindLabel === '프로'
            ? '프로젝트'
            : (scheduleRow.gradeLabel || '—').trim() || '—'
        : '—',
      programDotKind: scheduleRow
        ? scheduleRow.programKindLabel === '고정'
          ? 'fixed'
          : scheduleRow.programKindLabel === '프로'
            ? 'project'
            : 'track'
        : 'none',
      timetableText: scheduleRow?.timetableText ?? '',
      unitLabel: '',
      learningLabel: '',
      recordKind,
      kindLabel: noteType,
      note: noteBody,
      recordMemberGroupId: groupId || null,
      sourceMemberId: note.memberId,
    });
  }

  return out;
}
